import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";

import { ApiError } from "@tdelivr/api-client";
import type { Transport } from "@tdelivr/api-client";
import { createDispatchConsoleBffClient } from "@tdelivr/api-client/generated/experience/dispatch-console-bff.js";
import type {
  DeliveryCountFloor,
  DeliveryRow,
  SourceReport,
} from "@tdelivr/api-client/generated/experience/dispatch-console-bff.js";
import { stateClass } from "@tdelivr/design-tokens";

import {
  boundedNotes,
  countText,
  courierCell,
  courierGlyph,
  DELIVERY_TILES,
  deliveryClass,
  deliveryPage,
  floorMark,
  readableStatus,
  tileCount,
  timeInState,
  wallClock,
  type DeliveryOrder,
} from "../active-deliveries.ts";
import { stateForError, STALE_AFTER_MS, type ViewState } from "../view-state.ts";
import { PageHeading } from "./PageHeading.tsx";
import { StateBadge } from "./StateBadge.tsx";
import { StateView } from "./StateView.tsx";

/**
 * The active-deliveries board: every delivery in flight, with the courier carrying it.
 *
 * Built to `02 Console Shell v3.dc.html`, whose operations console opens on this page and is the
 * only design file that draws it — `s.page` defaults to `Active deliveries`, and `onDeliveries`
 * gates its four tiles, its seven columns, its brand toolbar, its two footers, its row detail
 * dialog and its `New delivery` form. Every measurement below is read from that file's inline
 * styles: the page body is padded `24px 28px 40px` with a 20px gap; the tile grid is four
 * `minmax(0, 1fr)` tracks 14px apart, each tile `16px 18px` inside a 14px radius with a 13px label,
 * a 36px glyph tile and a 30/36 count at `-0.02em`; the card is a 16px-radius `--surface` panel
 * holding a `12px 16px` brand toolbar, a `12px 14px` grid bar, a `10px 16px` `--sunken` header
 * strip, rows of `10px 16px` at a 60px minimum with a 14px gap, and two footers at `12px 16px` and
 * `10px 14px`.
 *
 * **One request, one scope.** EXP-008 composes the three layers — DEL-03's in-flight orders,
 * DEL-06's tracking projection for the courier, DRV-01's roster for that courier's name — with this
 * operator's own token, and publishes a verdict per layer whether it answered or not. R-09 is why
 * that is not a browser's job: DEL-03 publishes no courier on an order and DEL-06 publishes **no
 * listing at all**, so the design's `Courier` column is one request per row under a second scope on
 * a screen that refreshes.
 *
 * **The one thing this screen exists to get right.** `courierAbsence` has five values and only one
 * of them means nobody is carrying the delivery. The other four mean the aggregate could not find
 * out, and `not_requested` means specifically that the row fell past the bounded sweep. Drawing
 * them alike is the incident R-09 records under "the one that is worse than missing": an operator
 * reads a free courier and sends a second job to somebody already holding a parcel. So the one
 * takes the design's dashed `person` avatar and its own words — `Unassigned`, or `Awaiting courier`
 * on a row still waiting for pickup — and the four take a dashed `question_mark`, the word
 * `Not known`, and a second line naming which of the four it is. The dashed treatment is not
 * invented here: it is the design's own `unknown` state class, "borrows neither success nor
 * failure, because neither is true yet", and the same ring the live map puts on a driver whose job
 * it only infers.
 *
 * **Counts are floors unless the aggregate says otherwise.** R-04 measured that no listing on this
 * platform publishes a total, so `countsAreTotals` decides whether the design's four tile counts
 * and its `7 deliveries · 1 need a human · 1 failed` footer may be drawn as totals at all. When it
 * is false they are drawn `7+` with the `unknown` class's dashed treatment and said in words
 * underneath, and `countFloors` names the layer that bounded or withheld them. The design has no
 * treatment of its own for a floor; Q-43 records the question rather than letting this be a taste.
 *
 * **A layer can be lost without the board being lost.** DEL-06 refusing costs the courier cells and
 * nothing else — the rows, their states and their clocks are all DEL-03's — so the board renders
 * and says what is missing, which is the fix R-14 made to the live map. Only DEL-03 failing empties
 * the table, because only DEL-03 produces rows.
 *
 * **What the design draws that nothing publishes** is absent rather than estimated: the `Brand` and
 * `Route` columns, the brand filter, the `New delivery` form and the detail panel's `Pickup`,
 * `Drop-off` and `Assigned` rows. R-20 in `docs/design/DESIGN_RECONCILIATION.md` measures each.
 */

/** The three layers the aggregate reports on, by the names it gives them, and what each one is. */
const LAYERS_REPORTED = {
  deliveryOrders: "Deliveries",
  deliveryTracking: "Couriers",
  courierProfiles: "Courier names",
} as const satisfies Readonly<Record<string, string>>;

type ReportedLayer = keyof typeof LAYERS_REPORTED;

/** The one scope the page itself asks for, which grants the right to ask and nothing else. */
const GATE_SCOPE = ["dispatch-console:read"] as const;

/**
 * The scopes any command on this board would need.
 *
 * An operator holding none of them is view-only. `delivery:order:workload` is what
 * `POST /v1/tenants/{tenantId}/delivery-orders` requires and `delivery:order:operations` what its
 * transitions require — measured from `docs/design/api-operations.json`, not recalled.
 *
 * Unchanged by the aggregate, and that is the point of it: EXP-008 serves reads only, so
 * `dispatch-console:read` says nothing about whether this operator may act.
 */
const ACTING_SCOPES = ["delivery:order:workload", "delivery:order:operations"] as const;

/** The design's own rows-per-page choices, in its order. */
const PER_PAGE_CHOICES = [5, 10, 25, 50] as const;

/** The design's sort options, in its order and its words. */
const ORDER_CHOICES: readonly { readonly value: DeliveryOrder; readonly label: string }[] = [
  { value: "default", label: "Default order" },
  { value: "state", label: "State" },
  { value: "az", label: "Name A–Z" },
  { value: "za", label: "Name Z–A" },
];

/** The export formats the design's menu offers, in its order and its words. */
const EXPORTS = [
  { format: "csv", glyph: "description", label: "CSV" },
  { format: "xls", glyph: "table_view", label: "Excel (.xls)" },
  { format: "pdf", glyph: "picture_as_pdf", label: "PDF" },
] as const;

type ExportFormat = (typeof EXPORTS)[number]["format"];

/**
 * Why the design's `New delivery` form is drawn and refused.
 *
 * Measured rather than assumed. `POST /v1/delivery-quotes` takes a `productId` and pickup and
 * dropoff as `{ latitude, longitude }`; `POST /v1/tenants/{tenantId}/delivery-orders` takes a
 * `quoteId`, a `productId`, `senderPartyId`, `recipientPartyId`, `pickupAddressId` and
 * `deliveryAddressId`, all UUIDs. The form collects a sender's name, two street addresses, two
 * phone numbers, a size word and a service word — and no operation on this platform turns any of
 * those into any of these. There is no geocoder (G-15, R-08), no party creation reachable from this
 * console, and no product picker on the form. Wiring it would mean inventing a payload.
 */
const NEW_DELIVERY_WHY =
  "No published operation takes what this form collects: the quote wants a productId and coordinates, and the order wants party and address identifiers. See R-20.";

export interface ActiveDeliveriesProps {
  readonly transport: Transport;
  readonly tenantId: string;
  /** Innermost last: tenant, section, page. Rendered in the page body, never in the top bar. */
  readonly crumbs?: readonly string[];
  /**
   * The tenant's name, which the design's empty state says out loud.
   *
   * `No active deliveries for East Africa Mobility Ltd` rather than "No deliveries": an operator
   * who has switched tenant and forgotten needs the page to say whose board is empty. Falls back to
   * the first crumb, which is the same name.
   */
  readonly tenantName?: string;
  /** What this operator holds. Decides the view-only chip and whether the dialog's actions are live. */
  readonly scopes?: ReadonlySet<string>;
  /** Injected so the clocks and the stale threshold can be exercised without waiting. */
  readonly now?: () => number;
}

/** What one request to the aggregate left the board holding. */
interface Loaded {
  readonly rows: readonly DeliveryRow[];
  /** The aggregate's own watermark, which decides the stale strip and the `as of` row label. */
  readonly observedAt: number | undefined;
  readonly countsAreTotals: boolean;
  readonly countFloors: readonly DeliveryCountFloor[];
  readonly courierLookups: number;
  readonly degraded: boolean;
  /** One verdict per layer that did not answer, keyed by the layer the aggregate names. */
  readonly lost: Readonly<Partial<Record<ReportedLayer, ViewState>>>;
}

export function ActiveDeliveries({
  transport,
  tenantId,
  crumbs,
  tenantName,
  scopes,
  now,
}: ActiveDeliveriesProps): ReactNode {
  const [loaded, setLoaded] = useState<Loaded | undefined>(undefined);
  const [failure, setFailure] = useState<ViewState | undefined>(undefined);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState<string | undefined>(undefined);
  const [order, setOrder] = useState<DeliveryOrder>("default");
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState<number>(10);
  const [openedId, setOpenedId] = useState<string | undefined>(undefined);

  const load = useCallback(async () => {
    try {
      /*
       * One read.
       *
       * No `Promise.all` and no three clients: the three layers are settled inside EXP-008, which
       * never lets one of them fail the aggregate. What this `catch` handles is the case the
       * fan-out cannot — the aggregate itself refusing or being unreachable, which is one failure
       * rather than three.
       */
      const view =
        await createDispatchConsoleBffClient(transport).getDispatchConsoleDeliveries(tenantId);
      setLoaded({
        rows: view.rows,
        observedAt: Number.isFinite(Date.parse(view.observedAt))
          ? Date.parse(view.observedAt)
          : undefined,
        countsAreTotals: view.countsAreTotals,
        countFloors: view.countFloors,
        courierLookups: view.courierLookups,
        degraded: view.degraded,
        lost: lostLayers(view.sources),
      });
      setFailure(undefined);
    } catch (error) {
      /*
       * One state for the whole board, because there is no board.
       *
       * The aggregate answers 403 rather than 200 when every layer was refused, and puts the three
       * reports in `details.sources`. Those name the scopes to ask for; `dispatch-console:read` is
       * the gate this operator has already passed, so naming it would send them to ask for the one
       * permission they hold. It is only the right answer when the refusal carried no reports.
       */
      const refused = reportsIn(error);
      const wanted = refused === undefined ? [] : missingScopesIn(refused);
      setFailure(stateForError(error, wanted.length > 0 ? wanted : GATE_SCOPE));
      setLoaded(undefined);
    }
  }, [transport, tenantId]);

  useEffect(() => {
    void load();
  }, [load]);

  const at = now?.() ?? Date.now();

  const view = useMemo(
    () =>
      deliveryPage(loaded?.rows ?? [], {
        search,
        ...(status === undefined ? {} : { status }),
        order,
        page,
        perPage,
        now: at,
      }),
    [loaded, search, status, order, page, perPage, at],
  );

  /*
   * The table's own state, which is the orders layer's and nothing else's.
   *
   * DEL-06 refusing costs the courier cells; DRV-01 refusing costs the names beside the
   * identifiers. Neither empties the board, and a page that went to one state for any of the three
   * would throw away rows an operator can work. Only DEL-03 produces rows, so only DEL-03 decides
   * whether there is a table.
   *
   * `empty` is deliberately not among the states handed over. The design draws its own empty for
   * this page, with the tenant named in it, and that block is rendered below instead.
   */
  const stale =
    loaded?.observedAt !== undefined && at - loaded.observedAt >= STALE_AFTER_MS
      ? at - loaded.observedAt
      : undefined;
  const tableState: ViewState =
    failure ??
    loaded?.lost.deliveryOrders ??
    (loaded === undefined
      ? { kind: "loading" }
      : stale === undefined
        ? { kind: "ready", stale: false }
        : { kind: "ready", stale: true, ageMs: stale });

  const held = loaded?.rows ?? [];
  const countsAreTotals = loaded?.countsAreTotals ?? true;
  const filtering = search.trim().length > 0 || status !== undefined;
  const nothingAtAll = loaded !== undefined && held.length === 0 && failure === undefined;

  const viewOnly = scopes !== undefined && !ACTING_SCOPES.some((scope) => scopes.has(scope));
  const opened = view.all.find((row) => row.orderId === openedId);

  const clearFilters = (): void => {
    setSearch("");
    setStatus(undefined);
    setPage(1);
  };

  const exportMenu = (
    <ExportMenu
      rows={view.all}
      now={at}
      stale={stale !== undefined}
      observedAt={loaded?.observedAt}
    />
  );

  /*
   * The heading row, hoisted because the loading state draws it too.
   *
   * In `02 Console Shell v3` the headings are outside the state switch: the design shimmers the
   * rows beneath a heading row that never moves, so the board does not jump when the data lands.
   * Drawing a second copy inside the skeleton would be a second copy of this grid, free to
   * disagree with this one. Q-287.
   */
  const head = (
    <div className="deliveries__head">
      <span>State</span>
      <span>ID</span>
      <span className="deliveries__head--courier">Courier</span>
      <span className="deliveries__head--right">In state</span>
      <span />
    </div>
  );

  return (
    <section className="page deliveries">
      {loaded?.degraded === true ? (
        /*
         * The design puts this bar in the top bar, under the tenant chip. The shell owns the top bar
         * in this console and draws no banner, so it is page content here — the same move the live
         * map's `Live` pill and layer switches made, for the same reason, recorded in R-09.
         *
         * Its sentence is this board's rather than the design's. The design's reads "Driver
         * locations are delayed · Positions may be up to 90 s old (location service slow since 13:58
         * EAT)", which is sample content about the live map's location service. Naming the layer
         * that actually did not answer is the whole value of the banner.
         */
        <div className="deliveries__degraded" role="status">
          <span aria-hidden="true" className="deliveries__degraded-glyph">
            warning
          </span>
          <strong>Part of this board did not answer</strong>
          <span>{degradedSentence(loaded.lost)}</span>
        </div>
      ) : null}

      <PageHeading
        crumbs={crumbs ?? []}
        title="Active deliveries"
        /*
         * One code, because one service is read. The eight words of the design's own chip are not
         * lost: every layer names itself in its own `SourceReport`, and the strip at the foot
         * prints the ones that did not answer. R-08's rule — a screen naming a service it does not
         * read is worse than one naming nothing.
         */
        source="EXP-008"
        description="Updates live. Sorted so whatever needs a person comes first."
        /*
         * The two commands go in the heading's `aside`, not its `action`, and the difference is 8
         * pixels on every row of the page.
         *
         * `PageHeading` puts an `action` inside the title's own row, level with the title, which is
         * where `19 WP18 Dispatch Operations` draws its primary command. `02 Console Shell v3`
         * draws this page differently: its heading wrapper is `align-items: flex-end` with the text
         * block, a spacer and the buttons as siblings, so the buttons align to the bottom of the
         * whole block and the title's line stays 32 tall rather than 40. Measured both ways — in
         * the `action` slot the sentence under the title, the tiles and every row sat 8px low.
         */
        aside={
          <>
            {viewOnly ? (
              <span className="deliveries__role-note">
                <span aria-hidden="true" className="deliveries__role-glyph">
                  visibility
                </span>
                View only · delivery:order:read
              </span>
            ) : null}
            {exportMenu}
            <button
              type="button"
              className="deliveries__primary"
              // Drawn, and refused, exactly as the demand queue's `+ Manual assign` is and for the
              // same reason: a live button here would need a payload invented to reach it.
              disabled
              title={NEW_DELIVERY_WHY}
            >
              <span aria-hidden="true" className="deliveries__primary-glyph">
                add
              </span>
              New delivery
            </button>
          </>
        }
      />

      {/*
        The four tiles, and all four have a source — which no board in this console has managed
        before. DEL-03's in-flight set is exactly `delivery_attempted` (human), `failed`,
        `in_custody` and `accepted` (active and idle) and `pickup_ready` (waiting), and the design's
        own class table maps each. They are still floors whenever `countsAreTotals` is false.
      */}
      {nothingAtAll || tableState.kind !== "ready" ? null : (
        <div className="deliveries__tiles">
          {DELIVERY_TILES.map((tile) => {
            const counted = tileCount(held, tile, at);
            const urgent =
              (tile.classKey === "human" || tile.classKey === "failed") && counted.count > 0;
            return (
              <div
                key={tile.classKey}
                className={[
                  "deliveries__tile",
                  urgent ? `deliveries__tile--urgent-${tile.classKey}` : "",
                  countsAreTotals ? "" : "deliveries__tile--floor",
                ]
                  .filter(Boolean)
                  .join(" ")}
              >
                <div className="deliveries__tile-head">
                  <span className="deliveries__tile-label">{tile.label}</span>
                  <span
                    aria-hidden="true"
                    className={`deliveries__tile-glyph deliveries__tile-glyph--${tile.classKey}`}
                  >
                    {tile.glyph}
                  </span>
                </div>
                <span
                  className="deliveries__tile-count"
                  {...(countsAreTotals ? {} : { title: floorTitle(loaded?.countFloors ?? []) })}
                >
                  {counted.count === 0 ? "—" : floorMark(counted.count, countsAreTotals)}
                </span>
                <span className="deliveries__tile-foot">
                  <span
                    aria-hidden="true"
                    className={`deliveries__shape deliveries__shape--${stateClass(tile.classKey).glyph} deliveries__shape--on-${tile.classKey}`}
                  />
                  {counted.foot}
                </span>
              </div>
            );
          })}
        </div>
      )}

      <div
        className={
          stale === undefined ? "deliveries__panel" : "deliveries__panel deliveries__panel--stale"
        }
      >
        {/*
          The design's first toolbar row.

          Two of its three controls are gone and both absences are measured. The brand chips need a
          brand on a delivery, and DEL-03 publishes none — nor does anything it could be joined to,
          and TEN-01 publishes no payload at all (R-07). The `Filter by ID, place, courier` pill
          beside them is a display, not a control: the working search is in the row below, which the
          design also draws, so keeping both would be two search boxes where one works.
        */}
        <div className="deliveries__brandbar">
          <span className="deliveries__brandbar-spacer" />
          <button
            type="button"
            className="deliveries__filters"
            // Drawn and refused. DEL-03's listing publishes `status`, `limit` and `after` and no
            // other filter, and the one filter it does publish is already the toolbar's state
            // select below. R-01.
            disabled
            title="DEL-03's listing publishes one filter, status, and it is the state select beside this. See R-01."
          >
            <span aria-hidden="true" className="deliveries__filters-glyph">
              filter_list
            </span>
            Filters
          </button>
        </div>

        <div className="deliveries__toolbar">
          <span className="deliveries__search">
            <span aria-hidden="true" className="deliveries__search-glyph">
              search
            </span>
            <input
              id="active-deliveries-search"
              type="search"
              aria-label="Search"
              placeholder="Search"
              value={search}
              onChange={(event) => {
                setSearch(event.target.value);
                setPage(1);
              }}
            />
          </span>
          <select
            id="active-deliveries-status"
            aria-label="Filter by state"
            value={status ?? ""}
            onChange={(event) => {
              setStatus(event.target.value === "" ? undefined : event.target.value);
              setPage(1);
            }}
          >
            <option value="">All states</option>
            {view.states.map((value) => (
              <option key={value} value={value}>
                {value}
              </option>
            ))}
          </select>
          <select
            id="active-deliveries-order"
            aria-label="Sort"
            value={order}
            onChange={(event) => {
              setOrder(event.target.value as DeliveryOrder);
            }}
          >
            {ORDER_CHOICES.map((choice) => (
              <option key={choice.value} value={choice.value}>
                {choice.label}
              </option>
            ))}
          </select>
          {exportMenu}
        </div>

        <StateView
          state={tableState}
          purpose="Deliveries in flight appear here as soon as they are accepted."
          onRetry={() => {
            void load();
          }}
          skeleton={{
            head,
            // Five, which is what this board draws. The design's seven need `Brand` and `Route`,
            // which DEL-03 does not publish (R-20) — the same five tracks the ready state uses, so
            // the shimmer and the rows stand on one grid.
            columns: 5,
            rowClassName: "deliveries__row",
            page: "Active deliveries",
            // The expression the empty state already uses, for the same reason: an operator who
            // has switched tenant and forgotten needs the page to say whose board this is.
            tenant: tenantName ?? crumbs?.[0] ?? "this tenant",
          }}
        >
          {nothingAtAll ? null : (
            <>
              {head}
              <div className="deliveries__rows">
                {view.rows.map((row) => (
                  <Row
                    key={row.orderId}
                    row={row}
                    at={at}
                    {...(loaded?.observedAt === undefined ? {} : { observedAt: loaded.observedAt })}
                    stale={stale !== undefined}
                    onOpen={() => {
                      setOpenedId(row.orderId);
                    }}
                  />
                ))}
              </div>

              {/*
                The design's first footer: its own count sentence, and previous and next.

                The design draws the two controls as static spans beside a working pager below. They
                are buttons here, doing what the pager below does — a duplicated control that works
                is the design's geometry without a dead control in it.
              */}
              <div className="deliveries__count">
                <span
                  {...(countsAreTotals ? {} : { title: floorTitle(loaded?.countFloors ?? []) })}
                >
                  {countText(held, countsAreTotals)}
                </span>
                <span className="deliveries__count-controls">
                  <button
                    type="button"
                    disabled={page <= 1}
                    onClick={() => setPage((current) => Math.max(1, current - 1))}
                  >
                    Previous
                  </button>
                  <button
                    type="button"
                    disabled={page >= view.pageCount}
                    onClick={() => setPage((current) => Math.min(view.pageCount, current + 1))}
                  >
                    Next
                  </button>
                </span>
              </div>
            </>
          )}

          {view.total === 0 && !nothingAtAll ? (
            <div className="deliveries__no-match">
              <span aria-hidden="true" className="deliveries__no-match-glyph">
                search_off
              </span>
              Nothing matches this search or filter.
              <button type="button" className="deliveries__clear" onClick={clearFilters}>
                Clear filters
              </button>
            </div>
          ) : null}

          <div className="deliveries__pager">
            <span>
              {view.total === 0
                ? "No matching rows"
                : `Showing ${(page - 1) * perPage + 1}–${Math.min(page * perPage, view.total)} of ${floorMark(view.total, countsAreTotals || filtering)}`}
            </span>
            <span className="deliveries__pager-controls">
              <select
                id="active-deliveries-per-page"
                aria-label="Rows per page"
                value={perPage}
                onChange={(event) => {
                  setPerPage(Number(event.target.value));
                  setPage(1);
                }}
              >
                {PER_PAGE_CHOICES.map((choice) => (
                  <option key={choice} value={choice}>
                    {choice} per page
                  </option>
                ))}
              </select>
              <button
                type="button"
                aria-label="Previous page"
                disabled={page <= 1}
                onClick={() => setPage((current) => Math.max(1, current - 1))}
              >
                <span aria-hidden="true">chevron_left</span>
              </button>
              <span className="deliveries__page">
                Page {Math.min(page, view.pageCount)} of {view.pageCount}
              </span>
              <button
                type="button"
                aria-label="Next page"
                disabled={page >= view.pageCount}
                onClick={() => setPage((current) => Math.min(view.pageCount, current + 1))}
              >
                <span aria-hidden="true">chevron_right</span>
              </button>
            </span>
          </div>

          {nothingAtAll ? (
            /*
             * The design's own empty state, drawn here rather than handed to `StateView`, and
             * drawn *after* the pager because that is where the design puts it.
             *
             * `StateView` is the console's one answer for the six states and every other screen
             * uses it for this one too. The exception is made because the design writes this page's
             * empty state out in full and names the tenant in it, and CLAUDE.md section 1 makes
             * that copy the law for this screen. The brand clause of its second sentence is the one
             * thing dropped, because no delivery publishes a brand. R-20.
             */
            <div className="deliveries__empty">
              <span aria-hidden="true" className="deliveries__empty-glyph">
                local_shipping
              </span>
              <strong>No active deliveries for {tenantName ?? crumbs?.[0] ?? "this tenant"}</strong>
              <span className="deliveries__empty-body">
                Showing every state except delivered and cancelled. New deliveries appear here as
                soon as they are accepted.
              </span>
            </div>
          ) : null}
        </StateView>
      </div>

      {/*
        What this board is actually showing, said once.

        Three different facts and the aggregate publishes each: the set is DEL-03's in-flight one,
        the counts may be floors, and the courier sweep is bounded. Worst first, as
        `boundedNotes` orders them.
      */}
      {loaded === undefined ? null : (
        <div className="deliveries__bounded" role="status">
          {boundedNotes({
            rows: loaded.rows,
            countsAreTotals: loaded.countsAreTotals,
            countFloors: loaded.countFloors,
            courierLookups: loaded.courierLookups,
          }).map((note) => (
            <p key={note}>{note}</p>
          ))}
        </div>
      )}

      <LayerFailures lost={loaded?.lost ?? {}} />

      {opened === undefined ? null : (
        <DeliveryDetail
          row={opened}
          at={at}
          viewOnly={viewOnly}
          onClose={() => {
            setOpenedId(undefined);
          }}
        />
      )}
    </section>
  );
}

/* -------------------------------------------------------------------------------------------- */

/**
 * One row of the grid.
 *
 * The design's seven columns are five here. `Brand` needs a brand on a delivery, which DEL-03 does
 * not publish; `Route` needs `Westlands → Kilimani` out of `pickupAddressId` and
 * `deliveryAddressId`, which no read turns into a place and no geocoder exists for (G-15, R-08).
 * Both are absent rather than estimated, and the design's own track rule — the first column
 * `minmax(170px, 1fr)`, each other data column its own `minmax`, the age 84px and the chevron 28 —
 * is applied to the columns that have a source.
 *
 * Below 1240 the design hides the courier cell and drops it from the column list, which is what the
 * stylesheet does here. That is its decision rather than this screen's, and it is worth naming
 * because the courier cell is the reason this board has an aggregate: at 1024 the five absences are
 * not on the row at all, and they come back in the card stack below 760.
 */
function Row({
  row,
  at,
  observedAt,
  stale,
  onOpen,
}: {
  readonly row: DeliveryRow;
  readonly at: number;
  readonly observedAt?: number;
  readonly stale: boolean;
  readonly onOpen: () => void;
}): ReactNode {
  const courier = courierCell(row);
  const key = deliveryClass(row);
  const elapsed = timeInState(row, at);
  return (
    <button
      type="button"
      className={key === undefined ? "deliveries__row" : `deliveries__row deliveries__row--${key}`}
      onClick={onOpen}
    >
      <span className="deliveries__cell">
        <StateBadge code="DEL-03" status={row.status} label={readableStatus(row.status)} />
      </span>
      <span className="deliveries__id">{row.orderId}</span>
      <span className="deliveries__courier">
        <span
          aria-hidden="true"
          className={
            courier.initials === undefined
              ? `deliveries__avatar deliveries__avatar--${courier.absence ?? "unassigned"}`
              : "deliveries__avatar"
          }
        >
          {courier.initials ?? courierGlyph(courier.absence ?? "unassigned")}
        </span>
        <span
          className="deliveries__courier-text"
          {...(courier.title === undefined ? {} : { title: courier.title })}
        >
          <span
            className={
              courier.absence === undefined || courier.absence === "unassigned"
                ? "deliveries__courier-name"
                : "deliveries__courier-name deliveries__courier-name--unknown"
            }
          >
            {courier.name}
          </span>
          <span className="deliveries__courier-id">{courier.identifier}</span>
        </span>
      </span>
      <span className="deliveries__age">
        <span
          className={
            stale ? "deliveries__age-value deliveries__age--stale" : "deliveries__age-value"
          }
        >
          {elapsed ?? "—"}
        </span>
        <span className="deliveries__age-label">
          {stale && observedAt !== undefined ? `as of ${wallClock(observedAt)}` : "in state"}
        </span>
      </span>
      <span aria-hidden="true" className="deliveries__more">
        more_horiz
      </span>
    </button>
  );
}

/**
 * The row detail dialog the design opens when a row is clicked.
 *
 * Its field list is the design's own, and so is the absence of three of them: the design builds the
 * list and then filters out every row whose value is empty, so `Brand`, `Pickup` and `Drop-off`
 * simply do not appear when nothing publishes them. That is the designed empty treatment rather
 * than a decision taken here.
 *
 * The history is one entry where the design draws three. `Requested` needs DEL-03's `createdAt`,
 * which DEL-03 publishes and `DeliveryRow` does not forward — recorded in R-20 as a change to make
 * on the aggregate rather than one to work around here. `Assigned` has no source anywhere: neither
 * DEL-03 nor DEL-06 publishes when a courier took the parcel.
 */
function DeliveryDetail({
  row,
  at,
  viewOnly,
  onClose,
}: {
  readonly row: DeliveryRow;
  readonly at: number;
  readonly viewOnly: boolean;
  readonly onClose: () => void;
}): ReactNode {
  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return (): void => {
      document.removeEventListener("keydown", onKey);
    };
  }, [onClose]);

  const courier = courierCell(row);
  const elapsed = timeInState(row, at);
  const fields: readonly {
    readonly label: string;
    readonly value: string;
    readonly title?: string;
  }[] = [
    { label: "Delivery", value: row.orderId },
    {
      label: "Courier",
      value:
        row.courierId === null
          ? courier.name
          : `${courier.name}${courier.name === row.courierId ? "" : ` · ${row.courierId}`}`,
      ...(courier.title === undefined ? {} : { title: courier.title }),
    },
    { label: "State", value: readableStatus(row.status) },
  ];

  return (
    <div className="deliveries__overlay" onClick={onClose} role="presentation">
      <div
        role="dialog"
        aria-modal="true"
        aria-label={row.orderId}
        className="deliveries__dialog"
        onClick={(event) => {
          event.stopPropagation();
        }}
      >
        <div className="deliveries__dialog-head">
          <span aria-hidden="true" className="deliveries__dialog-icon">
            local_shipping
          </span>
          <span className="deliveries__dialog-title">
            <strong>{row.orderId}</strong>
            {/* The design writes `<brand> · DEL-06 delivery`. There is no brand, and the order is
                DEL-03's rather than DEL-06's — R-08's rule about naming what is actually read. */}
            <span className="deliveries__dialog-sub">DEL-03 delivery order</span>
            <StateBadge code="DEL-03" status={row.status} label={readableStatus(row.status)} />
          </span>
          <button
            type="button"
            aria-label="Close"
            className="deliveries__dialog-close"
            onClick={onClose}
          >
            <span aria-hidden="true">close</span>
          </button>
        </div>

        <div className="deliveries__dialog-body">
          <div className="deliveries__dialog-section">
            <span className="deliveries__dialog-legend">Details</span>
            <div className="deliveries__dialog-fields">
              {fields.map((field) => (
                <div key={field.label} className="deliveries__dialog-field">
                  <span className="deliveries__dialog-key">{field.label}</span>
                  <span
                    className="deliveries__dialog-value"
                    {...(field.title === undefined ? {} : { title: field.title })}
                  >
                    {field.value}
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div className="deliveries__dialog-section">
            <span className="deliveries__dialog-legend">History</span>
            <div className="deliveries__dialog-history">
              <div className="deliveries__dialog-event">
                <span aria-hidden="true" className="deliveries__dialog-dot" />
                <span className="deliveries__dialog-event-text">
                  <span className="deliveries__dialog-event-what">Last update</span>
                  <span className="deliveries__dialog-event-when">
                    {elapsed === undefined ? "—" : `${elapsed} in state`}
                  </span>
                </span>
              </div>
            </div>
          </div>

          <div className="deliveries__dialog-note">
            <span aria-hidden="true" className="deliveries__dialog-note-glyph">
              info
            </span>
            <span>
              Actions send expectedVersion. If someone else changed it first, you will see both
              versions.
            </span>
          </div>
        </div>

        <div className="deliveries__dialog-foot">
          <button type="button" className="deliveries__dialog-secondary" onClick={onClose}>
            Close
          </button>
          <button
            type="button"
            className="deliveries__dialog-secondary"
            // Real, and with nowhere to go. DEL-06 answers
            // `GET /v1/deliveries/{deliveryId}/tracking`, and this console has no per-delivery page
            // and no designed destination for what it returns. The same treatment the live map's
            // `Location history` gets, for the same reason.
            disabled
            title="DEL-06 answers this read, and this console has no designed page for what it returns. See R-20."
          >
            <span aria-hidden="true" className="deliveries__dialog-action-glyph">
              map
            </span>
            Track live
          </button>
          <button
            type="button"
            className="deliveries__dialog-primary"
            disabled
            title={
              viewOnly ? "Your role does not include delivery:order:workload." : NEW_DELIVERY_WHY
            }
          >
            <span aria-hidden="true" className="deliveries__dialog-action-glyph">
              edit
            </span>
            Edit
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * The export control, drawn twice because the design draws it twice.
 *
 * Once beside `New delivery` in the page head and once in the grid toolbar. Two instances of one
 * component rather than one menu reached from two buttons: each owns its own open state, so the
 * two cannot disagree about whether a menu is showing.
 */
function ExportMenu({
  rows,
  now,
  stale,
  observedAt,
}: {
  readonly rows: readonly DeliveryRow[];
  readonly now: number;
  readonly stale: boolean;
  readonly observedAt?: number | undefined;
}): ReactNode {
  const [open, setOpen] = useState(false);
  const region = useRef<HTMLSpanElement>(null);

  // The menu closes on Escape and on a click outside, which the design file never needed to handle
  // because it is a reference rather than something an operator uses for a whole shift.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === "Escape") setOpen(false);
    };
    const onPointer = (event: MouseEvent): void => {
      const element = region.current;
      if (element !== null && !element.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onPointer);
    return (): void => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onPointer);
    };
  }, [open]);

  return (
    <span className="deliveries__export" ref={region}>
      <button
        type="button"
        className="deliveries__export-button"
        aria-expanded={open}
        onClick={() => {
          setOpen((current) => !current);
        }}
      >
        <span aria-hidden="true" className="deliveries__export-glyph">
          download
        </span>
        Export
      </button>
      {open ? (
        <div className="deliveries__export-menu" role="menu">
          {EXPORTS.map((choice) => (
            <button
              key={choice.format}
              type="button"
              role="menuitem"
              className="deliveries__export-item"
              onClick={() => {
                setOpen(false);
                exportRows(rows, choice.format, {
                  now,
                  stale,
                  ...(observedAt === undefined ? {} : { observedAt }),
                });
              }}
            >
              <span aria-hidden="true" className="deliveries__export-item-glyph">
                {choice.glyph}
              </span>
              {choice.label}
            </button>
          ))}
        </div>
      ) : null}
    </span>
  );
}

/** Which layers are not on this board, named, with what each one would need. */
function LayerFailures({
  lost,
}: {
  readonly lost: Readonly<Partial<Record<ReportedLayer, ViewState>>>;
}): ReactNode {
  const missing = (Object.keys(LAYERS_REPORTED) as ReportedLayer[])
    // The orders layer is the table's own state and has already been said there; repeating it under
    // a board that is showing nothing would be the same sentence twice.
    .filter((layer) => layer !== "deliveryOrders" && lost[layer] !== undefined)
    .map((layer) => ({ label: LAYERS_REPORTED[layer], state: lost[layer] as ViewState }));
  if (missing.length === 0) return null;
  return (
    <div className="deliveries__lost" role="status">
      <span aria-hidden="true" className="deliveries__lost-glyph">
        layers_clear
      </span>
      <span>
        Not on this board: {missing.map((entry) => entry.label).join(", ")}.{" "}
        {missing.map((entry) => lostSentence(entry.state)).join(" ")} Every delivery is still here.
      </span>
    </div>
  );
}

/* -------------------------------------------------------------------------------------------- */

/**
 * Export what the operator is looking at, in the columns they are looking at.
 *
 * The filtered and ordered set rather than the page: an operator exporting a board wants the board,
 * not the ten rows that happened to be on screen. Excel takes an HTML table, which is what
 * `application/vnd.ms-excel` has always meant in practice, and PDF is the browser's own print
 * dialogue.
 *
 * The courier column carries the cell's two lines joined, so an exported row says `Not known · Not
 * asked` rather than a blank an operator would read as unassigned. That is the same hazard R-09
 * names, surviving into a spreadsheet somebody works off a printout of.
 */
function exportRows(
  rows: readonly DeliveryRow[],
  format: ExportFormat,
  clock: { readonly now: number; readonly stale: boolean; readonly observedAt?: number },
): void {
  const header = ["State", "Delivery", "Courier", "In state"];
  const ageLabel =
    clock.stale && clock.observedAt !== undefined ? ` (as of ${wallClock(clock.observedAt)})` : "";
  const body = rows.map((row) => {
    const courier = courierCell(row);
    return [
      readableStatus(row.status),
      row.orderId,
      `${courier.name} · ${courier.identifier}`,
      `${timeInState(row, clock.now) ?? "—"}${ageLabel}`,
    ];
  });

  if (format === "csv") {
    const quote = (value: string): string => `"${value.replaceAll('"', '""')}"`;
    const csv = [header, ...body].map((line) => line.map(quote).join(",")).join("\n");
    download(new Blob([csv], { type: "text/csv" }), "active-deliveries.csv");
    return;
  }

  const table =
    '<table border="1" cellpadding="6" style="border-collapse:collapse;font-family:sans-serif;font-size:12px">' +
    `<tr>${header.map((cell) => `<th>${escapeHtml(cell)}</th>`).join("")}</tr>` +
    body
      .map((line) => `<tr>${line.map((cell) => `<td>${escapeHtml(cell)}</td>`).join("")}</tr>`)
      .join("") +
    "</table>";

  if (format === "xls") {
    download(
      new Blob([`<html><head><meta charset="utf-8"></head><body>${table}</body></html>`], {
        type: "application/vnd.ms-excel",
      }),
      "active-deliveries.xls",
    );
    return;
  }

  const printable = window.open("", "_blank");
  if (printable === null) return;
  printable.document.write(
    `<html><head><title>active deliveries</title></head><body><h2 style="font-family:sans-serif">active deliveries</h2>${table}</body></html>`,
  );
  printable.document.close();
  printable.focus();
  printable.print();
}

function escapeHtml(value: string): string {
  return value.replaceAll("&", "&amp;").replaceAll("<", "&lt;").replaceAll(">", "&gt;");
}

function download(blob: Blob, name: string): void {
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = name;
  document.body.append(link);
  link.click();
  setTimeout(() => {
    URL.revokeObjectURL(link.href);
    link.remove();
  }, 500);
}

/**
 * One layer's verdict as a state this console already knows how to draw.
 *
 * The same six categories `stateForError` maps an `ApiError` onto, read off the report instead of
 * off a throw — because that is what the aggregate turned the throw into. The two authorization
 * statuses become `forbidden` carrying the scopes the report names, which is what makes a refusal
 * say *which* permission is missing on a screen that never saw the 403 itself.
 */
function lostLayers(
  reports: readonly SourceReport[],
): Readonly<Partial<Record<ReportedLayer, ViewState>>> {
  const lost: Partial<Record<ReportedLayer, ViewState>> = {};
  for (const report of reports) {
    if (!(report.layer in LAYERS_REPORTED)) continue;
    if (report.status === "ok") continue;
    lost[report.layer as ReportedLayer] =
      report.status === "forbidden" || report.status === "unauthorized"
        ? { kind: "forbidden", missingScopes: report.scopes }
        : {
            kind: "error",
            code: report.code ?? `${report.layer}_${report.status}`,
            message: `${report.service} did not answer`,
            // An answer this aggregate cannot read will read the same way next time; an outage may
            // not. The fan-out's own distinction, kept.
            retryable: report.status !== "invalid",
            offline: false,
          };
  }
  return lost;
}

/** What to do about a layer that is missing, from the state that says why it is missing. */
function lostSentence(state: ViewState): string {
  if (state.kind === "forbidden") {
    return state.missingScopes.length === 0
      ? "Your role does not include it."
      : `It needs ${state.missingScopes.join(" and ")}.`;
  }
  return state.kind === "error" ? `${state.message}.` : "";
}

/** The degraded bar's sentence: which layers are missing, in the words the board uses for them. */
function degradedSentence(lost: Readonly<Partial<Record<ReportedLayer, ViewState>>>): string {
  const names = (Object.keys(LAYERS_REPORTED) as ReportedLayer[])
    .filter((layer) => lost[layer] !== undefined)
    .map((layer) => LAYERS_REPORTED[layer]);
  if (names.length === 0) return "One of this board's three reads did not answer.";
  return `${names.join(" and ")} did not answer. Everything else on this page is current.`;
}

/** Why a count is a floor, for the `title` on every number that is one. */
function floorTitle(floors: readonly DeliveryCountFloor[]): string {
  if (floors.length === 0) return "This count is a floor rather than a total.";
  return floors
    .map((floor) =>
      floor.reason === "truncated"
        ? `${floor.service} had more deliveries than this board asked for, so this is a floor rather than a total.`
        : `${floor.service} did not answer, so this is a floor rather than a total.`,
    )
    .join(" ");
}

/** Every scope the refused layers named, deduplicated, so a refusal can be acted on by name. */
function missingScopesIn(reports: readonly SourceReport[]): string[] {
  const scopes = new Set<string>();
  for (const report of reports) {
    if (report.status !== "forbidden" && report.status !== "unauthorized") continue;
    for (const scope of report.scopes) scopes.add(scope);
  }
  return [...scopes].sort();
}

/**
 * The layer verdicts a refusal carried, if it carried any.
 *
 * Read defensively rather than cast: these come off an error body, so a report missing its layer,
 * its status or its scopes is dropped rather than turned into a sentence about an unnamed
 * permission.
 */
function reportsIn(error: unknown): readonly SourceReport[] | undefined {
  if (!(error instanceof ApiError)) return undefined;
  const sources = error.details?.["sources"];
  if (!Array.isArray(sources)) return undefined;
  const reports: SourceReport[] = [];
  for (const entry of sources as readonly unknown[]) {
    if (typeof entry !== "object" || entry === null) continue;
    const record = entry as Record<string, unknown>;
    const { layer, service, status, scopes } = record;
    if (typeof layer !== "string" || typeof service !== "string") continue;
    if (typeof status !== "string" || !Array.isArray(scopes)) continue;
    reports.push({
      layer,
      service,
      status: status as SourceReport["status"],
      scopes: scopes.filter((scope): scope is string => typeof scope === "string"),
      ...(typeof record["code"] === "string" ? { code: record["code"] } : {}),
    });
  }
  return reports.length === 0 ? undefined : reports;
}
